<template>
  <div class="transfer-section">
    <div class="grid-layout">
      <!-- Left: Lookup -->
      <div class="form-card">
        <div class="card-header">
          <div class="header-icon">
            <Icon name="search" :size="20" color="#1b92a6" />
          </div>
          <div>
            <h3 class="card-title">Find device</h3>
            <p class="card-subtitle">Lookup by IDI device ID or serial number</p>
          </div>
        </div>

        <div class="form-grid">
          <div class="lookup-toggle">
            <button
              type="button"
              class="lookup-toggle-btn"
              :data-active="lookupKind === 'idi_device_id' || null"
              @click="lookupKind = 'idi_device_id'"
            >
              IDI device ID
            </button>
            <button
              type="button"
              class="lookup-toggle-btn"
              :data-active="lookupKind === 'serial_number' || null"
              @click="lookupKind = 'serial_number'"
            >
              Serial number
            </button>
            <button
              type="button"
              class="lookup-toggle-btn"
              :data-active="lookupKind === 'deviceID' || null"
              @click="lookupKind = 'deviceID'"
              title="Local lookup in your current org — handy for legacy rows missing serial_number / idi_device_id"
            >
              deviceID
            </button>
          </div>

          <NumericInput
            type="text"
            :label="lookupLabel"
            :value="lookupValue"
            :placeholder="lookupPlaceholder"
            @update:value="(v: string) => (lookupValue = v)"
          />
        </div>

        <p v-if="lookupError" class="error_message mb-2">{{ lookupError }}</p>

        <div class="form-actions">
          <button
            class="generate-btn"
            type="button"
            :disabled="!lookupValue.trim() || lookupLoading"
            @click="lookup"
          >
            <Loader v-if="lookupLoading" />
            <span v-else>Lookup device</span>
          </button>
          <button class="clear-btn" type="button" :disabled="lookupLoading" @click="reset">
            Clear
          </button>
        </div>
      </div>

      <!-- Right: Match + Transfer -->
      <div class="form-card">
        <div class="card-header">
          <div class="header-icon">
            <Icon name="arrow_right" :size="20" color="#1b92a6" />
          </div>
          <div>
            <h3 class="card-title">Transfer to customer org</h3>
            <p class="card-subtitle">Stamps `transferred_from`, moves the row atomically</p>
          </div>
        </div>

        <div v-if="!matchedDevice" class="empty-match">
          <Icon name="info_circle" :size="20" color="var(--txt-secondary-1)" />
          <p>Look up a device on the left to start.</p>
        </div>

        <template v-else>
          <div class="match-panel">
            <div class="match-row">
              <span class="match-label">deviceID</span>
              <span class="match-value mono">{{ matchedDevice.deviceID }}</span>
            </div>
            <div class="match-row">
              <span class="match-label">deviceType</span>
              <span class="match-value">{{ matchedDevice.deviceType || '—' }}</span>
            </div>
            <div class="match-row">
              <span class="match-label">deviceName</span>
              <span class="match-value">{{ matchedDevice.deviceName || '—' }}</span>
            </div>
            <div class="match-row">
              <span class="match-label">idi_device_id</span>
              <span class="match-value mono">{{ matchedDevice.idi_device_id || '—' }}</span>
            </div>
            <div class="match-row">
              <span class="match-label">current org</span>
              <span class="match-value mono">{{ matchedDevice.organisationID }}</span>
            </div>
            <div class="match-row">
              <span class="match-label">transferred_from</span>
              <span class="match-value mono">{{ matchedDevice.transferred_from || '—' }}</span>
            </div>
            <div class="match-row">
              <span class="match-label">paired_at</span>
              <span class="match-value">{{ matchedDevice.paired_at || '—' }}</span>
            </div>
          </div>

          <div v-if="matchedDevice.paired_at" class="warn-banner">
            This device is already paired (paired_at set). Transferring may break its current
            pairing.
          </div>

          <!-- Deploy fields editor: master must populate these as part of
               the transfer so the customer's deploy flow has everything
               it needs. Customer-side wizard / device card both read
               these fields directly off the row. machine_name is mirrored
               to tailscale_id per the dock provisioning convention. -->
          <div class="fields-section">
            <div class="fields-header">
              <h4 class="fields-title">Device deploy fields</h4>
              <p class="fields-subtitle">
                Required for the customer's deploy flow. Written as part of the transfer. Machine
                name is also written to <code>tailscale_id</code>.
              </p>
            </div>
            <div class="form-grid">
              <NumericInput
                type="text"
                label="Machine name (Tailscale ID)"
                :value="fields.machineName"
                placeholder="e.g. spaceship"
                @update:value="(v: string) => (fields.machineName = v)"
              />
              <NumericInput
                type="text"
                label="Device IP"
                :value="fields.deviceIp"
                placeholder="e.g. 100.64.0.5"
                @update:value="(v: string) => (fields.deviceIp = v)"
              />
              <NumericInput
                type="text"
                label="MQTT Host"
                :value="fields.mqttHost"
                placeholder="e.g. mqtt.example.com"
                @update:value="(v: string) => (fields.mqttHost = v)"
              />
              <NumericInput
                type="text"
                label="MQTT Port"
                :value="fields.mqttPort"
                placeholder="e.g. 1883"
                @update:value="(v: string) => (fields.mqttPort = v)"
              />
            </div>
          </div>

          <div class="form-grid mt-4">
            <label class="select-label">Destination organisation</label>
            <SelectInput
              v-model="destinationOrgId"
              :options="orgOptions"
              :label="
                orgsQuery.isPending.value ? 'Loading organisations…' : 'Choose an organisation'
              "
              :disabled="orgsQuery.isPending.value"
              :is-searchable="true"
              max-height="260px"
            />
            <p v-if="orgsQuery.isError.value" class="error_message">
              Couldn't load organisations.
              <button type="button" class="retry-link" @click="orgsQuery.refetch()">Retry</button>
            </p>
          </div>

          <p v-if="transferError" class="error_message mb-2">{{ transferError }}</p>

          <div class="form-actions">
            <button
              class="generate-btn"
              type="button"
              :disabled="!isTransferReady || transferMutation.isPending.value"
              @click="transfer"
            >
              <Loader v-if="transferMutation.isPending.value" />
              <span v-else>Transfer device</span>
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useApi } from '@/config/apiFetcher'
import useOnboardingDevices from '@/composables/Device/useOnboardingDevices'
import useOrganisations from '@/composables/useOrganisations'
import { appStore } from '@/store/app'
import { useUserStore } from '@/store/userStore'
import { uuid } from '@/helpers'
import log from '@/utils/useLogger'
import Icon from '@/components/Icon.vue'
import Loader from '@/components/ui/Loader.vue'
import NumericInput from '@/components/Inputs/NumericInput.vue'
import SelectInput from '@/components/BaseComponents/SelectInput.vue'
import type { OnboardingDevice } from '@/types/onboarding.types'

const api = useApi()
const baseUrl = import.meta.env.VITE_APP_HTTP_ENPOINT
const { addNotifications } = appStore()
const { useUpdateDeviceMutation, useOrgDevicesQuery } = useOnboardingDevices()
const transferMutation = useUpdateDeviceMutation()
const { useOrganisationsQuery } = useOrganisations()
const orgsQuery = useOrganisationsQuery()
const { organisationId } = storeToRefs(useUserStore())
// Local lookup by deviceID — works for legacy rows that don't have
// serial_number / idi_device_id set yet. Limited to the operator's
// current org (the BE single-device endpoint is org-scoped anyway).
const orgDevicesQuery = useOrgDevicesQuery(organisationId)

type LookupKind = 'idi_device_id' | 'serial_number' | 'deviceID'

const lookupKind = ref<LookupKind>('idi_device_id')
const lookupValue = ref('')
const lookupLoading = ref(false)
const lookupError = ref('')

const lookupLabel = computed(() => {
  if (lookupKind.value === 'idi_device_id') return 'IDI device ID'
  if (lookupKind.value === 'serial_number') return 'Serial number'
  return 'deviceID (your org)'
})
const lookupPlaceholder = computed(() => {
  if (lookupKind.value === 'idi_device_id') return 'idids-29721'
  if (lookupKind.value === 'serial_number') return 'DJI-DOCK3-00041'
  return 'e.g. 86021229'
})

const matchedDevice = ref<OnboardingDevice | null>(null)
const destinationOrgId = ref('')
const transferError = ref('')

// Deploy-field editor state. The four fields below are required by
// every deploy path (customer wizard + post-onboard device card) and
// are written as part of the transfer in the same update-device call.
// machine_name doubles as tailscale_id per the dock provisioning
// convention (the hostname IS the Tailscale node identity).
const fields = reactive({
  machineName: '',
  deviceIp: '',
  mqttHost: '',
  mqttPort: '',
})

watch(
  matchedDevice,
  (d) => {
    fields.machineName = d?.machine_name || ''
    fields.deviceIp = d?.device_ip || ''
    fields.mqttHost = d?.mqtt_host || ''
    fields.mqttPort = d?.mqtt_port != null ? String(d.mqtt_port) : ''
  },
  { immediate: true },
)

const selectableOrgs = computed(() => {
  const list = orgsQuery.data.value ?? []
  if (!matchedDevice.value) return list
  // Hide the source org so ops can't accidentally "transfer" to the same org.
  return list.filter((o) => o.organisationID !== matchedDevice.value!.organisationID)
})

const orgOptions = computed(() =>
  selectableOrgs.value.map((o) => ({
    value: o.organisationID,
    label: `${o.organisationName || '(unnamed)'} — ${o.organisationID}`,
  })),
)

const lookup = async () => {
  const value = lookupValue.value.trim()
  if (!value) return
  lookupLoading.value = true
  lookupError.value = ''
  matchedDevice.value = null
  transferError.value = ''
  try {
    if (lookupKind.value === 'deviceID') {
      // Local lookup against the current org's device list — used for
      // legacy rows that pre-date serial_number / idi_device_id and
      // therefore can't be found via the BE single-device endpoint.
      if (orgDevicesQuery.isPending.value) {
        await orgDevicesQuery.refetch()
      }
      const list = orgDevicesQuery.data.value ?? []
      const device = list.find((d) => d.deviceID === value) || null
      if (!device) {
        lookupError.value = 'No device with that deviceID in your current org'
        return
      }
      matchedDevice.value = device
      return
    }
    const url = `${baseUrl}device-management/devices?${lookupKind.value}=${encodeURIComponent(value)}`
    const res = await api.get(url)
    const device = (res.data?.device ?? null) as OnboardingDevice | null
    if (!device) {
      lookupError.value = 'No device found'
      return
    }
    matchedDevice.value = device
  } catch (err: any) {
    const status = err?.response?.status
    if (status === 404) {
      lookupError.value = 'Device not found in any org you can access'
    } else if (status === 409) {
      lookupError.value =
        'This device is registered to another org. You must be authenticated as that org to transfer it out.'
    } else {
      lookupError.value = err?.response?.data?.error || err?.message || 'Lookup failed'
    }
    log.error('admin device lookup failed', err)
  } finally {
    lookupLoading.value = false
  }
}

const isTransferReady = computed(() => {
  if (!matchedDevice.value) return false
  const dest = destinationOrgId.value.trim()
  if (!dest) return false
  if (dest === matchedDevice.value.organisationID) return false
  // All four deploy fields must be filled in — they ship in the same
  // update-device call so the customer can deploy immediately.
  if (
    !fields.machineName.trim() ||
    !fields.deviceIp.trim() ||
    !fields.mqttHost.trim() ||
    !fields.mqttPort.trim()
  )
    return false
  const port = Number(fields.mqttPort)
  if (!Number.isFinite(port) || port <= 0) return false
  return true
})

const transfer = async () => {
  if (!matchedDevice.value || !isTransferReady.value) return
  transferError.value = ''
  const sourceOrg = matchedDevice.value.organisationID
  const destOrg = destinationOrgId.value.trim()
  const deviceID = matchedDevice.value.deviceID
  const machineName = fields.machineName.trim()
  try {
    await transferMutation.mutateAsync({
      from_organisationID: sourceOrg,
      organisationID: destOrg,
      deviceID,
      machine_name: machineName,
      tailscale_id: machineName,
      device_ip: fields.deviceIp.trim(),
      mqtt_host: fields.mqttHost.trim(),
      mqtt_port: Number(fields.mqttPort),
    })
    addNotifications({
      id: uuid(10),
      notification: `Transferred ${deviceID} from ${sourceOrg} to ${destOrg}`,
      type: 'success',
    })
    matchedDevice.value = null
    lookupValue.value = ''
    destinationOrgId.value = ''
  } catch (err: any) {
    const status = err?.response?.status
    if (status === 404) {
      transferError.value = 'Source device not found (may have been moved already).'
    } else if (status === 409) {
      transferError.value = 'A device with this ID already exists in the destination org.'
    } else {
      transferError.value = err?.response?.data?.error || err?.message || 'Transfer failed'
    }
    log.error('admin device transfer failed', err)
  }
}

const reset = () => {
  lookupValue.value = ''
  lookupError.value = ''
  matchedDevice.value = null
  destinationOrgId.value = ''
  transferError.value = ''
}
</script>

<style scoped>
.transfer-section {
  padding-bottom: 2rem;
}

.grid-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

@media (max-width: 1200px) {
  .grid-layout {
    grid-template-columns: 1fr;
  }
}

.form-card {
  background: linear-gradient(145deg, var(--idi-shade0-100) 0%, rgba(11, 14, 18, 0.95) 100%);
  border: 1px solid var(--idi-shade3-50);
  border-radius: var(--rad-xl);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
}
.header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(27, 146, 166, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.card-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--txt-primary);
  margin: 0;
}
.card-subtitle {
  font-size: 0.75rem;
  color: var(--txt-secondary-1);
  margin: 2px 0 0;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.lookup-toggle {
  display: flex;
  background: var(--idi-shade2-100);
  border: 1px solid var(--idi-shade3-50);
  border-radius: var(--rad-lg);
  padding: 4px;
  gap: 4px;
}
.lookup-toggle-btn {
  flex: 1;
  background: transparent;
  border: none;
  color: var(--txt-secondary-1);
  font-size: 0.8125rem;
  font-weight: 600;
  padding: 8px 12px;
  border-radius: var(--rad-m);
  cursor: pointer;
  font-family: var(--primary-font);
  transition: all 0.15s;
}
.lookup-toggle-btn:hover {
  color: var(--txt-primary);
}
.lookup-toggle-btn[data-active] {
  background: var(--idi-shade3-50);
  color: var(--txt-primary);
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 4px;
}
.generate-btn {
  flex: 1;
  background: #1b92a6;
  color: #fff;
  border: none;
  border-radius: var(--rad-m);
  padding: 10px 16px;
  font-weight: 600;
  font-size: 0.875rem;
  font-family: var(--primary-font);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: opacity 0.15s;
}
.generate-btn:hover:not(:disabled) {
  opacity: 0.9;
}
.generate-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.clear-btn {
  background: transparent;
  border: 1px solid var(--idi-shade3-100);
  color: var(--txt-secondary-1);
  border-radius: var(--rad-m);
  padding: 10px 16px;
  font-weight: 600;
  font-size: 0.875rem;
  font-family: var(--primary-font);
  cursor: pointer;
}
.clear-btn:hover:not(:disabled) {
  background: var(--idi-shade3-50);
  color: var(--txt-primary);
}
.clear-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.empty-match {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 16px;
  border: 1px dashed var(--idi-shade3-50);
  border-radius: var(--rad-xl);
  color: var(--txt-secondary-1);
  font-size: 0.8125rem;
  text-align: center;
}

.match-panel {
  background: var(--idi-shade2-100);
  border: 1px solid var(--idi-shade3-50);
  border-radius: var(--rad-lg);
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.match-row {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 8px;
  align-items: baseline;
  font-size: 0.8125rem;
}
.match-label {
  color: var(--txt-secondary-2);
  text-transform: uppercase;
  font-size: 0.6875rem;
  letter-spacing: 0.04em;
  font-weight: 600;
}
.match-value {
  color: var(--txt-primary);
  word-break: break-all;
}
.match-value.mono {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 0.75rem;
  color: var(--txt-secondary-1);
}

.warn-banner {
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
  color: #f59e0b;
  border-radius: var(--rad-m);
  padding: 10px 12px;
  font-size: 0.75rem;
  line-height: 1.5;
  margin-top: 12px;
}

.fields-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--idi-shade3-50);
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.fields-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.fields-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--txt-primary);
  margin: 0;
}
.fields-subtitle {
  font-size: 0.75rem;
  color: var(--txt-secondary-1);
  margin: 0;
}
.fields-subtitle code {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 0.6875rem;
  background: var(--idi-shade3-25);
  padding: 1px 4px;
  border-radius: 3px;
}

.error_message {
  color: #ef4444;
  font-size: 0.8125rem;
}

.select-label {
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--txt-secondary-2);
  font-weight: 600;
  margin-bottom: -4px;
}
.retry-link {
  background: none;
  border: none;
  color: #1b92a6;
  text-decoration: underline;
  font-size: 0.8125rem;
  cursor: pointer;
  padding: 0 0 0 4px;
  font-family: var(--primary-font);
}

.mt-4 {
  margin-top: 16px;
}
.mb-2 {
  margin-bottom: 8px;
}
</style>
